/**
 * @typedef {{ size: number; targets: WeakMap<Element, (intersectionEntry: IntersectionObserverEntry) => boolean>; serializedOptions: string; }} ObserverCacheEntry
 * @typedef {{ unobserve: () => void; }} ObserveIntersectionController
 */

import $ from 'jquery';

/**
 * @type {Map<string, IntersectionObserverMultiton>}
 */
const observers = new Map();

export default class IntersectionObserverMultiton extends IntersectionObserver {

	/**
	 * @param {IntersectionObserverInit} observerInit
	 * @param {string} serializedOptions
	 *
	 * @returns {IntersectionObserverMultiton}
	 */
	static getOrCreateObserver(observerInit, serializedOptions) {

		if (!observers.has(serializedOptions)) {

			const observerData = {
				size: 0,
				targets: new WeakMap(),
				serializedOptions,
			};

			const observer = new this((/** @type {IntersectionObserverEntry[]} */ entries) => {

				const { targets } = observerData;

				for (const entry of entries) {
					// Some older browsers (Edge 12) didn't implement isIntersecting
					if (typeof entry.isIntersecting === 'undefined') {
						// @ts-ignore
						entry.isIntersecting = entry.intersectionRatio > 0;
					}
					// Some useful properties
					// const ratio = entry.intersectionRatio;
					// if (ratio === 0) {
					// 	entry.postion = 'out';
					// }
					// else if (ratio < 1) {
					// 	entry.postion = (entry.boundingClientRect.top < entry.intersectionRect.top) ? 'top' : 'bottom';
					// }
					// else {
					// 	entry.postion = 'in';
					// }

					const { target } = entry;

					// Get the callback from the WeakMap
					if (targets.has(target)) {

						const onChange = targets.get(target);

						// Call the onChange callback and if it returns true, unobserve the element
						if (onChange(entry)) {
							observer.unobserve(target);
						}
					}
					else {
						observer.unobserve(target);
						// console.warn('%cIntersectionObserver unobserved [MISSING] (size: %s, target: %o)', 'color: red; background: orange; font-weight: bold;', observerData.size, target);
					}
				}
			}, observerInit, observerData);

			// console.debug('%cIntersectionObserver created (options: %o, serializedOptions: %s)', 'color: violet; background: white', observerInit, serializedOptions);

			observers.set(serializedOptions, observer);
			return observer;
		}

		return observers.get(serializedOptions);
	}

	/**
	 * @param {IntersectionObserverCallback} callback
	 * @param {IntersectionObserverInit} options
	 * @param {Partial<ObserverCacheEntry>} observerData
	 */
	constructor(callback, options, observerData) {

		if (!observerData || !observerData.serializedOptions) {
			throw new Error('IntersectionObserverMultiton must be instantiated with IntersectionObserverMultiton.getOrCreateObserver()');
		}

		super(callback, options);
		this.observerData = observerData;
	}

	/**
	 * @param {Element} target
	 * @param {(intersectionEntry: IntersectionObserverEntry) => boolean} onChange
	 */
	// @ts-ignore
	observe(target, onChange) {
		const { observerData } = this;
		// Observer has been disconnected
		if (!observerData) {
			return;
		}
		observerData.size++;
		observerData.targets.set(target, onChange);
		super.observe(target);
		// console.log('%cIntersectionObserver observed (size: %s, target: %o)', 'color: green; background: white; font-weight: bold;', observerData.size, target);

		$(target).one('destroy.mp_inter_om', () => {
			// Observer might already be disconnected
			if (observerData?.targets?.has?.(target)) {
				this.unobserve(target);
			}
		});
	}

	/**
	 * @param {Element} target
	 */
	unobserve(target) {
		const { observerData } = this;
		// Observer has been disconnected
		if (!observerData) {
			return;
		}
		// console.warn('%cIntersectionObserver unobserved (size: %s, target: %o)', 'color: red; background: yellow; font-weight: bold;', observerData?.size, target);
		observerData.size--;
		observerData.targets.delete(target);
		$(target).off('destroy.mp_inter_om');
		super.unobserve(target);

		// eslint-disable-next-line unicorn/consistent-destructuring
		if (observerData.size <= 0) {
			this.disconnect();
		}
	}

	disconnect() {
		const { observerData } = this;
		// Observer has been disconnected
		if (!observerData) {
			return;
		}
		observerData.size = 0;
		observerData.targets = null;
		super.disconnect();
		observers.delete(observerData.serializedOptions);
		observerData.serializedOptions = null;
		this.observerData = null;
		// console.warn('%cIntersectionObserver disconnected (size: %s)', 'color: green; background: white; font-weight: bold;', observerData.size);
	};
}